import React, { useEffect, useRef, useState } from 'react';
import { Message, User, SenderType } from '../messages.types';

interface MessageContentProps {
	messages: Message[];
	currentUser: User;
	onReply?: () => void;
	onLoadMore?: () => void;
	hasMore?: boolean;
	isLoading?: boolean;
	selectedType?: 'parent' | 'student';
}

const MessageContent: React.FC<MessageContentProps> = ({ messages, onReply, onLoadMore, selectedType, hasMore = false, isLoading = false }) => {
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const [prevMessageCount, setPrevMessageCount] = useState(0);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const handleLoadMore = () => {
		if (onLoadMore) {
			setIsLoadingMore(true);
			onLoadMore();
		}
	};

	useEffect(() => {
		if (isLoadingMore && messages.length > prevMessageCount) {
			setIsLoadingMore(false);

			const newMessagesCount = messages.length - prevMessageCount;

			if (messagesContainerRef.current && newMessagesCount > 0) {
				const firstMessageElement = messagesContainerRef.current.querySelector('.message-wrapper');
				if (firstMessageElement) {
					setTimeout(() => {
						firstMessageElement.scrollIntoView({ block: 'center' });
					}, 100);
				}
			}
		} else if (!isLoadingMore && messages.length > prevMessageCount) {
			if (messagesEndRef.current) {
				messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
			}
		}

		setPrevMessageCount(messages.length);
	}, [messages, isLoadingMore, prevMessageCount, selectedType]);

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return 'Bugün';
		}
		if (date.toDateString() === yesterday.toDateString()) {
			return 'Dün';
		}

		return date.toLocaleDateString('tr-TR', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
	};

	const groupedMessages: { [key: string]: Message[] } = {};

	messages.forEach(message => {
		const date = message.date;
		if (!groupedMessages[date]) {
			groupedMessages[date] = [];
		}
		groupedMessages[date].push(message);
	});

	return (
		<div className="chat-content">
			{hasMore && (
				<div className="load-more-container">
					<button
						className="load-more-button"
						onClick={handleLoadMore}
						disabled={isLoading}>
						<i className="icons-dialog"></i>
						Önceki Konuşmalarım
					</button>
				</div>
			)}

			{isLoading && (
				<div className="loading-indicator">
					<div className="spinner"></div>
					<span>Yükleniyor...</span>
				</div>
			)}

			{Object.keys(groupedMessages).map(date => (
				<React.Fragment key={date}>
					<div className="message-date-divider">
						<span>{formatDate(date)}</span>
					</div>

					{groupedMessages[date].map(message => {
						const isOutgoing = message.sender === SenderType.USER;

						return (
							<div
								key={message.id}
								className={`message-wrapper ${isOutgoing ? 'outgoing' : 'incoming'}`}>
								<div className="message">
									<span translate="no">{message.content}</span>
								</div>

								<div className="message-time">{message.time}</div>
							</div>
						);
					})}
				</React.Fragment>
			))}

			<div ref={messagesEndRef} />

			{onReply && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						padding: '10px 0'
					}}>
					<button
						className="reply-button"
						onClick={onReply}>
						<i className="icons-reply"></i>
						Yanıtla
					</button>
				</div>
			)}
		</div>
	);
};

export default MessageContent;
